// cartService.js
import { extractIdFromGid } from "./categories-divide";

const CART_KEY = "cart";

// Get cart from local storage
export const getCart = () => {
  const cart = localStorage.getItem(CART_KEY);
  return cart ? JSON.parse(cart) : [];
};

// Set cart to local storage
export const setCart = (cart) => {
  localStorage.setItem(CART_KEY, JSON.stringify(cart));
};

// Add item to cart
export const addItemToCart = (item) => {
  const cart = getCart();
  cart.push(item);
  setCart(cart);
};

// Remove item from cart by index
export const removeItemFromCart = (index) => {
  const cart = getCart();
  cart.splice(index, 1);
  setCart(cart);
};

// Clear cart
export const clearCart = () => {
  localStorage.removeItem(CART_KEY);
};

/** Normalize product id (GID or numeric) to a comparable string. */
export const normalizeProductId = (id) => {
  if (id == null || id === "") return "";
  const asString = String(id);
  if (asString.includes("/")) {
    return String(extractIdFromGid(asString) || asString);
  }
  return asString;
};

/** Compare selectedOption arrays (variant identity). */
export const optionsMatch = (a = [], b = []) => {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  return a.every((value, index) => String(value) === String(b[index]));
};

/**
 * Find index of cart line matching product id + selected options.
 * Returns -1 if none. If duplicates exist, returns the first match.
 */
export const findCartItemIndex = (cart = [], productId, selectedOption = []) => {
  const normalizedId = normalizeProductId(productId);
  if (!normalizedId) return -1;
  return cart.findIndex(
    (item) =>
      normalizeProductId(item?.id) === normalizedId &&
      optionsMatch(item?.selectedOption, selectedOption),
  );
};

export const findCartItem = (cart = [], productId, selectedOption = []) => {
  const index = findCartItemIndex(cart, productId, selectedOption);
  return index >= 0 ? cart[index] : null;
};

/**
 * Upsert a cart line by id + selectedOption (same schema, no new fields).
 * Replaces the matched line; collapses any duplicate matches to one.
 */
export const upsertCartItem = (cart = [], item) => {
  if (!item) return [...cart];
  const normalizedId = normalizeProductId(item.id);
  const nextItem = { ...item, id: normalizedId || item.id };
  const matchIndexes = [];
  cart.forEach((line, index) => {
    if (
      normalizeProductId(line?.id) === normalizeProductId(nextItem.id) &&
      optionsMatch(line?.selectedOption, nextItem.selectedOption)
    ) {
      matchIndexes.push(index);
    }
  });

  if (matchIndexes.length === 0) {
    return [...cart, nextItem];
  }

  const [keepIndex, ...duplicateIndexes] = matchIndexes;
  const nextCart = cart.map((line, index) =>
    index === keepIndex ? nextItem : line,
  );
  // Remove duplicates from the end so indexes stay valid
  return nextCart.filter((_, index) => !duplicateIndexes.includes(index));
};

/**
 * Product-level summary for listing badges (any pack size for this product).
 */
export const getProductCartSummary = (cart = [], productId) => {
  const normalizedId = normalizeProductId(productId);
  const lines = cart.filter(
    (item) => normalizeProductId(item?.id) === normalizedId,
  );
  const totalQty = lines.reduce(
    (sum, line) => sum + (Number(line?.quantity) || 0),
    0,
  );
  return {
    inCart: lines.length > 0,
    lineCount: lines.length,
    totalQty,
    lines,
  };
};

/**
 * Collapse duplicate lines (same id + selectedOption), summing quantities.
 * Useful once when loading Cart for carts created before merge.
 */
export const dedupeCartLines = (cart = []) => {
  const result = [];
  cart.forEach((item) => {
    const index = findCartItemIndex(
      result,
      item?.id,
      item?.selectedOption || [],
    );
    if (index === -1) {
      result.push(item);
      return;
    }
    const existing = result[index];
    const quantity =
      (Number(existing?.quantity) || 0) + (Number(item?.quantity) || 0);
    const priceAsNumber = parseFloat(item?.price ?? existing?.price) || 0;
    result[index] = {
      ...existing,
      ...item,
      quantity,
      totalPrice: quantity * priceAsNumber,
    };
  });
  return result;
};
